import { computeLayout, ansichtLayout, GATE_DEFAULTS, LUNAR_LAMELLA, GATE_BOTTOM, SECTION_COLORS } from "./planner";

export const POST_EXTRA = 20; // Pfosten 20 mm höher als Zaun/Tor
export const FENCE_BOTTOM = 50; // Bodenluft Zaun
export const PUBLIC_WERK = { lamella: { ...LUNAR_LAMELLA, bottom: FENCE_BOTTOM } };
const postHeightOf = (h) => h + FENCE_BOTTOM + POST_EXTRA;
export const HIDDEN_MODELS = ["DOPPELSTABMATTE_656"];

export const PROJECT_TYPES = {
  fence: { label: "Gartenzaun", hint: "Grundstücksgrenze, Vorgarten, Einfahrt – mit oder ohne Tore" },
  balcony: { label: "Balkon", hint: "Balkongeländer – gerade, L- oder U-Form, auch mehrere Balkone" },
  stairs: { label: "Treppe", hint: "Treppengeländer – schräge Länge von erster bis letzter Stufe" },
  gate: { label: "Tor", hint: "Einzelnes Tor – Gehtüre, Doppelflügeltor oder Schiebetor" },
};
export const FORMS = { I: "Gerade (I)", L: "L-Form", U: "U-Form", closed: "Rundum geschlossen", free: "Freie Form zeichnen" };
export const FORM_SIDES = { I: ["A"], L: ["A", "B"], U: ["B", "A", "C"], closed: ["A", "B"], free: [] };

export function newProject(type, height) {
  const id = crypto.randomUUID();
  if (type === "stairs") return { id, type, length: 3000, steps: 12, height };
  if (type === "gate") return { id, type, gateType: "gehtuere", width: GATE_DEFAULTS.gehtuere.width, height, sockel: 0, posts: "alu", motor: false };
  const base = { id, type, form: "I", dims: { A: 6000, B: 3000, C: 3000 }, points: [], closed: false, gates: [], height };
  if (type === "balcony") return base;
  return { ...base, montage: "fundament", sockel: { height: 300, pfeiler: false, pfeilerWidth: 400, lichte: 1800 } };
}

export const newGate = (seg = 0, offset = 500) => ({ id: crypto.randomUUID(), seg, type: "gehtuere", width: GATE_DEFAULTS.gehtuere.width, offset, pos: "links", posts: "alu", motor: false });
export const hasMotor = (type) => type === "doppelflugel" || type === "schiebetor";
// Beschreibung eines Tores für Zusammenfassung / PDF (ohne Preise)
export function gateText(g, betweenPfeiler = false) {
  const parts = [];
  if (g.pos) parts.push(g.pos === "rechts" ? "rechts" : "links");
  parts.push(betweenPfeiler || g.posts === "beton" ? "zwischen Betonpfeilern" : "Alu-Torpfosten");
  if (hasMotor(g.type || g.gateType)) parts.push(g.motor ? "mit Antrieb" : "ohne Antrieb");
  return `${GATE_DEFAULTS[g.type || g.gateType]?.label || g.type}, Lichte ${g.width} mm (${parts.join(", ")})`;
}

export function shapePoints(p) {
  const { A = 0, B = 0, C = 0 } = p.dims || {};
  switch (p.form) {
    case "I": return { points: [{ x: 0, y: 0 }, { x: A, y: 0 }], closed: false };
    case "L": return { points: [{ x: 0, y: 0 }, { x: A, y: 0 }, { x: A, y: B }], closed: false };
    case "U": return { points: [{ x: 0, y: B }, { x: 0, y: 0 }, { x: A, y: 0 }, { x: A, y: C }], closed: false };
    case "closed": return { points: [{ x: 0, y: 0 }, { x: A, y: 0 }, { x: A, y: B }, { x: 0, y: B }], closed: true };
    default: return { points: p.points || [], closed: !!p.closed };
  }
}

export function projectPlan(p, base) {
  const { points, closed } = shapePoints(p);
  const segCount = Math.max(0, closed ? points.length : points.length - 1);
  return { ...base, height: p.height || base.height, postHeight: postHeightOf(p.height || base.height), werk: PUBLIC_WERK, segLabels: FORM_SIDES[p.form] || [], points, closed, gates: (p.gates || []).filter((g) => g.seg < segCount) };
}

// Ansicht (Vorderansicht) eines einzelnen Tor-Projekts für die Vorschau
export function gateAnsicht(p, base) {
  const h = p.height || base.height || 1000;
  const beton = p.posts === "beton";
  const pw = 400;
  const gateItem = { id: p.id, kind: "feld", width: Number(p.width) || 0, sockel: 0, alu: !beton, gate: p.gateType, gateHeight: h, postsOnSockel: false };
  const pf = (id) => ({ id, kind: "pfeiler", width: pw, height: h + 100 });
  const items = beton ? [pf(`${p.id}-pl`), gateItem, pf(`${p.id}-pr`)] : [gateItem];
  const plan = { ...base, height: h, postHeight: postHeightOf(h), werk: PUBLIC_WERK, ansicht: { items, wandStart: false, wandEnd: false } };
  return { plan, layout: ansichtLayout(plan) };
}

export function projectLayout(p, base) {
  if (p.type === "gate") {
    const W = Number(p.width) || 0, gd = base.posts.gate.dim, L = W + 2 * gd, beton = p.posts === "beton";
    return { segments: W ? [{ idx: 0, label: "A", a: { x: 0, y: 0 }, b: { x: L, y: 0 }, length: L, elements: [{ kind: "post", profile: "gate", from: 0, to: gd }, { kind: "gate", type: p.gateType, width: W, from: gd, to: gd + W }, { kind: "post", profile: "gate", from: gd + W, to: L }] }] : [],
      totalLength: L, fieldCount: 0, postCount: beton ? 0 : 2, gates: W ? [{ type: p.gateType, width: W }] : [], fields: [], posts: [], warnings: [] };
  }
  if (p.type === "stairs") {
    const L = Number(p.length) || 0;
    return { segments: L ? [{ idx: 0, label: "A", a: { x: 0, y: 0 }, b: { x: L, y: 0 }, length: L, elements: [{ kind: "field", width: L, from: 0, to: L }] }] : [],
      totalLength: L, fieldCount: L ? 1 : 0, postCount: 0, gates: [], fields: L ? [{ width: L, count: 1 }] : [], posts: [], warnings: [] };
  }
  return computeLayout(projectPlan(p, base));
}

// Bokocrt za ogradu: po stranici Pfeiler/Felder/Tore (links/rechts) → ansichtLayout
export function sockelItems(p, sideLen, sideIdx) {
  const onSockel = p.montage === "sockel";
  const s = onSockel ? (p.sockel || {}) : { height: 0, pfeiler: false };
  const sockel = Number(s.height) || 0, pw = Number(s.pfeilerWidth) || 400, lichte = Number(s.lichte) || 1800;
  const gates = (p.gates || []).filter((g) => g.seg === sideIdx);
  const items = [];
  const pf = (id) => ({ id, kind: "pfeiler", width: pw, height: sockel + (p.height || 0) + 100 });
  const pushPf = (id) => { if (items[items.length - 1]?.kind !== "pfeiler") items.push(pf(id)); };
  const isBeton = (g) => !!s.pfeiler || g.posts === "beton";
  // Toroberkante = Zaunoberkante: Sockel + Bodenluft Zaun + Zaunhöhe − Bodenluft Tor
  const pushGate = (g) => {
    if (isBeton(g)) pushPf(`${g.id}-pl`);
    items.push({ id: g.id, kind: "feld", width: g.width, sockel: 0, alu: !isBeton(g), gate: g.type, gateHeight: (p.height || 0) + sockel + FENCE_BOTTOM - (GATE_BOTTOM[g.type] ?? FENCE_BOTTOM), postsOnSockel: true });
    if (isBeton(g)) items.push(pf(`${g.id}-pr`));
  };
  if (s.pfeiler) pushPf(`${p.id}-p0`);
  gates.filter((g) => g.pos !== "rechts").forEach(pushGate);
  const fieldAt = items.length;
  gates.filter((g) => g.pos === "rechts").forEach(pushGate);
  const used = items.reduce((a, i) => a + (Number(i.width) || 0), 0);
  const rest = Math.max(0, sideLen - used);
  const fields = [];
  if (s.pfeiler) {
    const n = Math.max(1, Math.round(rest / (lichte + pw)));
    const fw = Math.max(300, Math.round((rest - n * pw) / n));
    for (let i = 0; i < n; i++) fields.push({ id: `${p.id}-f${i}`, kind: "feld", width: fw, sockel, alu: false, gate: null, split: false }, pf(`${p.id}-p${i + 1}`));
  } else if (rest > 0) {
    fields.push({ id: `${p.id}-f`, kind: "feld", width: rest, sockel, alu: true, gate: null, split: true });
  }
  items.splice(fieldAt, 0, ...fields);
  return items;
}

export function sockelSides(p, base) {
  const lay = projectLayout(p, base);
  return lay.segments.map((seg) => {
    const items = sockelItems(p, seg.length, seg.idx);
    const plan = { ...base, height: p.height || base.height, postHeight: postHeightOf(p.height || base.height), werk: PUBLIC_WERK, ansicht: { items, wandStart: false, wandEnd: false } };
    return { label: seg.label, length: seg.length, plan, layout: ansichtLayout(plan) };
  });
}

export function projectSummary(p) {
  const d = p.dims || {};
  const sides = (FORM_SIDES[p.form] || []).map((k) => `${k} ${d[k]} mm`).join(" · ");
  if (p.type === "stairs") return `Treppe: schräge Länge ${p.length} mm, ${p.steps || "?"} Stufen, Geländerhöhe ${p.height} mm`;
  if (p.type === "gate") return `Tor: ${gateText({ ...p, type: p.gateType, pos: null })}, Höhe ${p.height} mm`;
  if (p.type === "balcony") return `Balkon ${FORMS[p.form]}: ${sides || `${p.points?.length || 0} Punkte`}, Höhe ${p.height} mm${p.handrail ? ", mit Handlauf" : ", ohne Handlauf"}`;
  const pfeiler = p.montage === "sockel" && !!p.sockel?.pfeiler;
  const m = p.montage === "sockel" ? `auf Sockel ${p.sockel?.height} mm${pfeiler ? `, Betonpfeiler ${p.sockel.pfeilerWidth} mm, Lichte ca. ${p.sockel.lichte} mm` : ", Alu-Pfosten auf Sockel"}` : "auf Fundament (Alu-Pfosten)";
  const sideName = (g) => (FORM_SIDES[p.form] || [])[g.seg] || String.fromCharCode(65 + (g.seg || 0));
  const g = (p.gates || []).map((x) => `Seite ${sideName(x)}: ${gateText(x, pfeiler)}`).join("; ");
  return `Gartenzaun ${FORMS[p.form]}: ${sides || `${p.points?.length || 0} Punkte`}, Höhe ${p.height} mm, ${m}${g ? `, Tore: ${g}` : ""}`;
}

// Skica za upit: svi projekti u jednom sketch-u (pomaknuti da se ne preklapaju)
export function buildSketch(projects, base) {
  const segments = []; let totalLength = 0, fieldCount = 0, postCount = 0, offX = 0;
  const ansicht = [];
  projects.forEach((p, pi) => {
    const lay = projectLayout(p, base);
    const xs = lay.segments.flatMap((s) => [s.a.x, s.b.x]);
    const minX = xs.length ? Math.min(...xs) : 0, maxX = xs.length ? Math.max(...xs) : 0;
    lay.segments.forEach((s) => segments.push({ idx: segments.length, label: `${pi + 1}${s.label}`, a: { x: s.a.x - minX + offX, y: s.a.y }, b: { x: s.b.x - minX + offX, y: s.b.y }, length: s.length, elements: s.elements }));
    offX += (maxX - minX) + 2000;
    totalLength += lay.totalLength; fieldCount += lay.fieldCount; postCount += lay.postCount;
    if (p.type === "fence") {
      sockelSides(p, base).forEach((sd) => { const seg = sd.layout.segments[0]; if (seg) ansicht.push({ project: pi + 1, side: sd.label, montage: p.montage, elements: seg.elements, length: seg.length, height: sd.plan.height, postHeight: sd.plan.postHeight, bottom: FENCE_BOTTOM }); });
    }
  });
  return { segments, totalLength, fieldCount, postCount, projects: projects.map((p, i) => ({ nr: i + 1, type: p.type, summary: projectSummary(p), form: p.form, dims: p.dims, height: p.height, handrail: p.handrail, montage: p.montage, sockel: p.sockel, length: p.length, steps: p.steps, gates: p.gates, gateType: p.gateType, width: p.width })), ansicht };
}

// Wandelt öffentliche Projekte (Anfrage) in interne Planer-Abschnitte (Sections) um
export function projectsToSections(plan) {
  const base = { ...plan };
  const projects = plan.projects || [];
  return projects.map((p, i) => {
    const h = p.height || base.height;
    let points = [], closed = false, gates = [];
    if (p.type === "gate") {
      const gd = base.posts?.gate?.dim || 100;
      const W = Number(p.width) || 0;
      const L = W + 2 * gd;
      points = [{ x: 0, y: 0 }, { x: L, y: 0 }];
      gates = [{ id: crypto.randomUUID(), seg: 0, type: p.gateType || "gehtuere", width: W, offset: gd }];
    } else if (p.type === "stairs") {
      points = [{ x: 0, y: 0 }, { x: Number(p.length) || 3000, y: 0 }];
    } else {
      const sp = shapePoints(p);
      points = sp.points; closed = sp.closed;
      const segCount = Math.max(0, closed ? points.length : points.length - 1);
      gates = (p.gates || []).filter((g) => g.seg < segCount).map((g) => ({ id: g.id || crypto.randomUUID(), seg: g.seg, type: g.type || g.gateType || "gehtuere", width: g.width, offset: g.offset ?? 500 }));
    }
    const names = { fence: "Gartenzaun", balcony: "Balkon", stairs: "Treppe", gate: "Tor" };
    return {
      id: crypto.randomUUID(), name: `${names[p.type] || "Abschnitt"} ${i + 1}`, color: SECTION_COLORS[i % SECTION_COLORS.length],
      model: base.model, variant: base.variant || null, height: h, postHeight: postHeightOf(h), maxField: base.maxField || 2000,
      posts: base.posts, points, closed, gates, view: "grundriss", ansicht: { items: [], wandStart: false, wandEnd: false },
    };
  });
}
